import type { PropsWithChildren } from 'react'
import { createContext, useContext } from 'react'
import type { TenantRef } from './tenantDecision'

/**
 * The tenant of the current host, for code outside React (the data provider).
 * Set by TenantGate before it renders the app.
 */
export const tenantRef: { current: string | undefined } = { current: undefined }

const ActiveTenantContext = createContext<TenantRef | null>(null)

export function ActiveTenantProvider({
  tenant,
  children,
}: PropsWithChildren<{ tenant: TenantRef }>) {
  // Written during render on purpose: it must be set before the children's
  // effects and queries run (the data provider reads it). One tenant per page
  // load, so there is no concurrent-render hazard.
  tenantRef.current = tenant.id
  return <ActiveTenantContext value={tenant}>{children}</ActiveTenantContext>
}

/** The tenant of the current host. Only inside TenantGate. */
export function useActiveTenant(): TenantRef {
  const tenant = useContext(ActiveTenantContext)
  if (tenant === null) {
    throw new Error('useActiveTenant() outside <TenantGate>')
  }
  return tenant
}

const BaseDomainContext = createContext<string | null>(null)

/** The admin domain (`VITE_ADMIN_BASE_DOMAIN`), for links to tenant hosts. */
export function BaseDomainProvider({
  baseDomain,
  children,
}: PropsWithChildren<{ baseDomain: string }>) {
  return <BaseDomainContext value={baseDomain}>{children}</BaseDomainContext>
}

export function useBaseDomain(): string {
  const baseDomain = useContext(BaseDomainContext)
  if (baseDomain === null) {
    throw new Error('useBaseDomain() outside <BaseDomainProvider>')
  }
  return baseDomain
}
